import React from "react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { CalendarEvent } from "../../utils/calendarUtils";
import {
  getEventsForDay,
  EVENT_COLORS,
  getEntityRoute,
  EVENT_TYPE_LABELS,
} from "../../utils/calendarUtils";

interface DayViewProps {
  currentDate: Date;
  events: CalendarEvent[];
}

const DayView: React.FC<DayViewProps> = ({ currentDate, events }) => {
  const navigate = useNavigate();
  const dayEvents = getEventsForDay(events, currentDate).sort((a, b) => {
    if (a.startTime && b.startTime)
      return a.startTime.localeCompare(b.startTime);
    if (a.startTime) return -1;
    if (b.startTime) return 1;
    return a.title.localeCompare(b.title);
  });

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      {/* Day header */}
      <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
        <h3 className="text-lg font-semibold text-gray-900">
          {format(currentDate, "EEEE, d MMMM yyyy")}
        </h3>
        <p className="text-sm text-gray-500">
          {dayEvents.length} event{dayEvents.length !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Events list */}
      <div className="divide-y divide-gray-100">
        {dayEvents.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-gray-400">
            No events on this day
          </div>
        ) : (
          dayEvents.map((event) => {
            const colors = EVENT_COLORS[event.type];
            return (
              <button
                key={event.id}
                onClick={() =>
                  navigate(getEntityRoute(event.type, event.entityId))
                }
                className={`flex w-full items-start gap-3 border-l-4 ${colors.border} p-4 text-left transition-colors hover:bg-gray-50`}
              >
                {/* Time column */}
                <div className="w-16 flex-shrink-0">
                  {event.startTime ? (
                    <>
                      <div className="text-sm font-medium text-gray-900">
                        {event.startTime}
                      </div>
                      {event.endTime && (
                        <div className="text-xs text-gray-500">
                          {event.endTime}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-xs text-gray-400">All day</div>
                  )}
                </div>

                {/* Event details */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${colors.bg} ${colors.text}`}
                    >
                      {EVENT_TYPE_LABELS[event.type]}
                    </span>
                    {event.status && (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                        {event.status}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-sm font-medium text-gray-900">
                    {event.title}
                  </div>
                  {event.amount !== undefined && (
                    <div className="mt-0.5 text-sm text-gray-600">
                      £
                      {(event.amount / 100).toLocaleString("en-GB", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DayView;
